import { useEffect, useRef, useCallback } from 'react';

// Derive WebSocket URL from REACT_APP_API_URL so a single .env change covers everything.
// e.g. http://172.20.10.5:8000/api  →  ws://172.20.10.5:8000/ws/notifications/
const _apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const WS_URL = _apiUrl.replace(/^http/, 'ws').replace(/\/api\/?$/, '') + '/ws/notifications/';

/**
 * Hook WebSocket — URL dérivée automatiquement depuis REACT_APP_API_URL.
 * Reconnexion automatique avec backoff exponentiel (5s → 10s → 20s → 60s max).
 */
export default function useWebSocket(onMessage, enabled = true) {
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const retryRef = useRef(0);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const connect = useCallback(() => {
    if (!enabled) return;
    if (wsRef.current && wsRef.current.readyState < 2) return; // already open/connecting

    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0; // reset backoff on success
      };

      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type !== 'pong') onMessageRef.current(data);
        } catch {}
      };

      let pingInterval;
      ws.onopen = () => {
        retryRef.current = 0;
        pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
        }, 30000);
      };

      ws.onclose = () => {
        clearInterval(pingInterval);
        // Exponential backoff: 5s, 10s, 20s, 40s, 60s max
        const delay = Math.min(5000 * Math.pow(2, retryRef.current), 60000);
        retryRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {}
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    // Slight delay on mount to avoid connect before page fully loads
    timerRef.current = setTimeout(connect, 1000);
    return () => {
      clearTimeout(timerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on deliberate close
        wsRef.current.close();
      }
    };
  }, [connect, enabled]);
}
